import { useId, type HTMLAttributes, type KeyboardEvent, type ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, Info, Inbox, XCircle } from 'lucide-react'
import { cx } from '../lib/cx'

/* ------------------------------------------------------------------ */
/* En-tête de page                                                     */
/* ------------------------------------------------------------------ */

export interface PageHeaderProps {
  title: ReactNode
  eyebrow?: ReactNode
  description?: ReactNode
  actions?: ReactNode
}

export function PageHeader({ title, eyebrow, description, actions }: PageHeaderProps) {
  return (
    <header className="page-head">
      <div>
        {eyebrow && <p className="page-eyebrow">{eyebrow}</p>}
        <h1 className="page-title">{title}</h1>
        {description && <p className="page-desc">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  )
}

/* ------------------------------------------------------------------ */
/* Onglets (WAI-ARIA tabs, flèches gauche/droite)                      */
/* ------------------------------------------------------------------ */

export interface TabItem {
  value: string
  label: ReactNode
}

export interface TabsProps {
  items: TabItem[]
  value: string
  onChange: (value: string) => void
  label: string
}

export function Tabs({ items, value, onChange, label }: TabsProps) {
  const baseId = useId()

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
    const index = items.findIndex((item) => item.value === value)
    const delta = event.key === 'ArrowRight' ? 1 : -1
    const next = items[(index + delta + items.length) % items.length]
    if (next) {
      onChange(next.value)
      document.getElementById(`${baseId}-${next.value}`)?.focus()
    }
  }

  return (
    <div className="tabs" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {items.map((item) => {
        const selected = item.value === value
        return (
          <button
            key={item.value}
            id={`${baseId}-${item.value}`}
            type="button"
            role="tab"
            className="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(item.value)}
          >
            {item.label}
          </button>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Filtres, état vide, libellé/valeur, alertes                         */
/* ------------------------------------------------------------------ */

export function FilterRow({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx('filter-row', className)} role="search" {...rest} />
}

export interface EmptyProps {
  title: ReactNode
  description?: ReactNode
  icon?: ReactNode
  action?: ReactNode
}

export function Empty({ title, description, icon, action }: EmptyProps) {
  return (
    <div className="empty">
      {icon ?? <Inbox aria-hidden="true" />}
      <p className="empty-title">{title}</p>
      {description && <p>{description}</p>}
      {action}
    </div>
  )
}

export function InfoGrid({ className, ...rest }: HTMLAttributes<HTMLDListElement>) {
  return <dl className={cx('info-grid', className)} style={{ margin: 0 }} {...rest} />
}

export function InfoItem({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <dt className="info-label">{label}</dt>
      <dd className="info-value" style={{ marginInlineStart: 0 }}>
        {children}
      </dd>
    </div>
  )
}

const ALERT_ICONS = { info: Info, success: CheckCircle2, warning: AlertTriangle, danger: XCircle }

export interface AlertProps {
  tone?: keyof typeof ALERT_ICONS
  title?: ReactNode
  children?: ReactNode
  className?: string
}

export function Alert({ tone = 'info', title, children, className }: AlertProps) {
  const Icon = ALERT_ICONS[tone]
  return (
    <div
      className={cx('alert', `alert-${tone}`, className)}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <Icon aria-hidden="true" />
      <div>
        {title && <strong>{title}</strong>}
        {children && <div>{children}</div>}
      </div>
    </div>
  )
}
